/**
 * @file Rocket entity factory.
 *
 * A rocket is a bullet that is slower, fatter, trails smoke and carries a blast
 * it hands to whatever ends its flight. It deliberately reuses the projectile
 * pipeline — same movement, same swept collision, same range retirement — so
 * the only genuinely new part is the `Explosive` component riding along.
 *
 * It never steers: a homing missile would remove the one thing this weapon is
 * about, which is committing to an aim a second and a half before it lands.
 */

import * as THREE from 'three';
import {
  Transform,
  Velocity,
  Collider,
  Renderable,
  Projectile,
  Explosive,
  Trail,
  Lifetime,
  PlayerControlled
} from '../components/index.js';
import { Layer } from '../config/Layers.js';

/**
 * Spawns a missile travelling along a direction.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @param {object} options Missile setup.
 * @param {THREE.Vector3} options.position Muzzle position.
 * @param {THREE.Vector3} options.direction Normalised travel direction.
 * @param {import('../components/Weapon.js').Weapon} options.weapon Weapon that fired it.
 * @param {import('../ecs/Entity.js').Entity} options.owner Shooter.
 * @param {number} [options.damage] Direct-hit damage, after buffs.
 * @param {number} [options.explosionDamage] Blast damage at the centre, after buffs.
 * @returns {import('../ecs/Entity.js').Entity}
 */
export function createRocket(ctx, { position, direction, weapon, owner, damage, explosionDamage }) {
  const { world, meshFactory } = ctx;
  const blast = weapon.explosive;

  const entity = world.createEntity('rocket');

  const transform = entity.add(new Transform(position.x, position.y, position.z));
  transform.rotationY = Math.atan2(direction.x, direction.z);

  const velocity = entity.add(new Velocity(weapon.projectileSpeed, 0, 0));
  velocity.linear.copy(direction).multiplyScalar(weapon.projectileSpeed);
  velocity.steered = false;

  entity.add(
    new Projectile({
      damage: damage ?? weapon.damage,
      range: weapon.range,
      ownerId: owner.id,
      // Never pierces: the first thing it touches is where it goes off.
      pierce: 0
    })
  );

  // The stock `rocket_launcher` row is a player archetype (PLAYER_BULLET). An AI
  // carrier reuses that row, so flip the collision pair or the missile passes
  // straight through the player and only ever hits walls/other enemies.
  const { layer, mask } = rocketCollisionFor(owner, weapon);
  entity.add(Collider.fromRadius(weapon.projectileRadius, layer, mask));

  entity.add(
    new Explosive({
      radius: blast.radius,
      damage: explosionDamage ?? blast.damage,
      mask: blast.mask,
      ownerId: owner.id,
      selfDamageMultiplier: blast.selfDamageMultiplier,
      shake: blast.shake,
      detonateOnDestroy: true
    })
  );

  if (weapon.trail) entity.add(new Trail(weapon.trail));
  // Backstop only: the range check retires the missile well before this.
  if (weapon.projectileLifetime > 0) entity.add(new Lifetime(weapon.projectileLifetime));

  entity.add(
    new Renderable(weapon.projectilePrefab, meshFactory.acquire(weapon.projectilePrefab), {
      yOffset: 0,
      faceVelocity: true
    })
  );

  return entity;
}

/**
 * Resolves the collider layer/mask a missile should fly with for this owner.
 *
 * @param {import('../ecs/Entity.js').Entity} owner Shooter.
 * @param {import('../components/Weapon.js').Weapon} weapon Weapon that fired it.
 * @returns {{ layer: number, mask: number }}
 */
function rocketCollisionFor(owner, weapon) {
  if (owner.has(PlayerControlled) || weapon.projectileLayer !== Layer.PLAYER_BULLET) {
    return { layer: weapon.projectileLayer, mask: weapon.projectileMask };
  }

  return {
    layer: Layer.ENEMY_BULLET,
    mask: Layer.WALL | Layer.PLAYER
  };
}
